/**
 * Masters Vuex Getters
 * Computed state accessors for the master tag import feature.
 */

import { getFieldsForTagType } from '../config/systemFields';
import { WIZARD_STEPS, calculateProgress } from '../config/wizardSteps';

export default {
  // === Batch Data ===
  GET_BATCHES: state => state.batches,
  GET_BATCHES_TOTAL: state => state.batchesTotal,
  GET_CURRENT_BATCH: state => state.currentBatch,
  GET_CURRENT_BATCH_TAGS: state => state.currentBatchTags,
  GET_CURRENT_BATCH_TAGS_TOTAL: state => state.currentBatchTagsTotal,

  // === Loading States ===
  GET_LOADING: state => state.loading,
  GET_IMPORTING: state => state.importing,
  GET_DELETING: state => state.deleting,

  // === Error State ===
  GET_ERROR: state => state.error,

  // === Wizard State ===
  GET_WIZARD_OPEN: state => state.wizardOpen,
  GET_WIZARD_STEP: state => state.wizardStep,
  GET_SELECTED_TAG_TYPE: state => state.selectedTagType,
  GET_SELECTED_SCOPE_TYPE: state => state.selectedScopeType,
  GET_SELECTED_TARGET_ID: state => state.selectedTargetId,
  GET_BATCH_NAME: state => state.batchName,
  GET_PARSED_DATA: state => state.parsedData,
  GET_EXCEL_COLUMNS: state => state.excelColumns,
  GET_COLUMN_MAPPINGS: state => state.columnMappings,
  GET_CUSTOM_ATTRIBUTE_SELECTIONS: state => state.customAttributeSelections,
  GET_CUSTOM_DISPLAY_NAMES: state => state.customDisplayNames,
  GET_CUSTOM_DATA_TYPES: state => state.customDataTypes,
  GET_SOURCE_FILE_NAME: state => state.sourceFileName,

  // === Wizard UI Computed Properties ===

  /**
   * Get system fields for the selected tag type.
   */
  GET_VISIBLE_SYSTEM_FIELDS: state => {
    // Return empty array if no tag type selected (prevents null passed to getFieldsForTagType)
    if (!state.selectedTagType) {
      return [];
    }
    return getFieldsForTagType(state.selectedTagType);
  },

  /**
   * Get count of mapped system fields.
   */
  GET_MAPPED_FIELDS_COUNT: state => {
    return Object.values(state.columnMappings).filter(Boolean).length;
  },

  /**
   * Get count of selected custom attributes.
   */
  GET_SELECTED_CUSTOM_COUNT: state => {
    return Object.values(state.customAttributeSelections).filter(Boolean).length;
  },

  /**
   * Calculate mapping progress percentage.
   */
  GET_MAPPING_PROGRESS: (state, getters) => {
    const systemFields = getters.GET_VISIBLE_SYSTEM_FIELDS;
    if (!systemFields.length) return 0;
    return Math.round((getters.GET_MAPPED_FIELDS_COUNT / systemFields.length) * 100);
  },

  /**
   * Calculate overall wizard progress percentage.
   */
  GET_WIZARD_PROGRESS: state => {
    return calculateProgress(state.wizardStep);
  },

  /**
   * Check if on configuration step.
   */
  IS_CONFIGURATION_STEP: state => state.wizardStep === WIZARD_STEPS.CONFIGURATION,

  /**
   * Check if on mapping step.
   */
  IS_MAPPING_STEP: state => state.wizardStep === WIZARD_STEPS.COLUMN_MAPPING,

  /**
   * Check if on review step.
   */
  IS_REVIEW_STEP: state => state.wizardStep === WIZARD_STEPS.REVIEW,

  /**
   * Check if on success step.
   */
  IS_SUCCESS_STEP: state => state.wizardStep === WIZARD_STEPS.SUCCESS,

  // === Duplicate Handling ===
  GET_DUPLICATE_CHECK_RESULT: state => state.duplicateCheckResult,
  GET_DUPLICATE_HANDLING: state => state.duplicateHandling,

  // === Background Job ===
  GET_PENDING_IMPORT_JOB: state => state.pendingImportJob,
  IS_BACKGROUND_IMPORT_PENDING: state => !!state.pendingImportJob,

  // === Import Result ===
  GET_IMPORT_RESULT: state => state.importResult,

  // === Import Progress (SSE streaming) ===
  GET_IMPORT_PROGRESS: state => state.importProgress,

  /**
   * Calculate import progress percentage.
   * Returns 0-100 based on processed/total.
   */
  GET_IMPORT_PROGRESS_PERCENT: state => {
    if (!state.importProgress || !state.importProgress.total) return 0;
    return Math.round((state.importProgress.processed / state.importProgress.total) * 100);
  },

  // === Pagination ===
  GET_OPTIONS: state => state.options,
  GET_PAGINATION: state => ({
    page: state.options.page,
    itemsPerPage: state.options.itemsPerPage,
    total: state.batchesTotal,
    pageCount: Math.ceil(state.batchesTotal / state.options.itemsPerPage) || 1,
  }),

  // === Tags Dialog ===
  GET_TAGS_DIALOG_OPEN: state => state.tagsDialogOpen,

  // === Computed Getters ===

  /**
   * Filter batches by tag type.
   */
  GET_BATCHES_BY_TYPE: state => tagType =>
    state.batches.filter(b => b.tagType === tagType),

  /**
   * Filter batches by scope (Facility or Group).
   */
  GET_BATCHES_BY_SCOPE: state => scopeType =>
    state.batches.filter(b =>
      scopeType === 'Facility'
        ? b.facilityId && !b.facilityGroupId
        : b.facilityGroupId
    ),

  /**
   * Get unmapped Excel columns (columns not mapped to system fields).
   */
  GET_UNMAPPED_COLUMNS: state => {
    const mappedColumns = Object.values(state.columnMappings).filter(Boolean);
    return state.excelColumns.filter(col => !mappedColumns.includes(col));
  },

  /**
   * Get columns selected as custom attributes.
   */
  GET_SELECTED_CUSTOM_COLUMNS: state =>
    Object.entries(state.customAttributeSelections)
      .filter(([, selected]) => selected)
      .map(([col]) => col),

  /**
   * Check if all required fields are mapped.
   */
  IS_MAPPING_VALID: state => {
    // name is required (camelCase key from shared propertyMapper)
    return !!state.columnMappings['name'];
  },

  /**
   * Get total record count from parsed data.
   */
  GET_PARSED_DATA_COUNT: state => state.parsedData.length,

  /**
   * Get rows that have valid values in required fields (name).
   * Returns all rows if 'name' is not yet mapped.
   */
  GET_VALID_ROWS: state => {
    const nameExcelColumn = state.columnMappings['name'];

    // If 'name' not mapped yet, return all rows
    if (!nameExcelColumn) {
      return state.parsedData;
    }

    // Filter to rows with valid 'name' values
    return state.parsedData.filter(row => {
      const nameValue = row[nameExcelColumn];
      return nameValue !== undefined && nameValue !== null && String(nameValue).trim() !== '';
    });
  },

  /**
   * Get count of valid rows (those with required fields).
   */
  GET_VALID_ROWS_COUNT: (state, getters) => {
    return getters.GET_VALID_ROWS.length;
  },

  /**
   * Get count of filtered (invalid) rows.
   */
  GET_FILTERED_ROWS_COUNT: (state, getters) => {
    return state.parsedData.length - getters.GET_VALID_ROWS.length;
  },

  /**
   * Check if wizard can proceed to next step.
   */
  CAN_PROCEED_TO_STEP: state => step => {
    switch (step) {
      case 2: // From Config to Upload
        return state.selectedTagType && state.selectedScopeType && state.selectedTargetId;
      case 3: // From Upload to Mapping
        return state.parsedData.length > 0 && state.excelColumns.length > 0;
      case 4: // From Mapping to Preview (camelCase key from shared propertyMapper)
        return !!state.columnMappings['name'];
      default:
        return true;
    }
  },

  /**
   * Get import statistics from result.
   */
  GET_IMPORT_STATS: state => {
    if (!state.importResult) return null;
    return {
      total: state.importResult.totalRecords || 0,
      success: state.importResult.successCount || 0,
      skipped: state.importResult.skippedCount || 0,
      merged: state.importResult.mergedCount || 0,
      failed: state.importResult.failedCount || 0,
      hasErrors: (state.importResult.errors?.length || 0) > 0,
    };
  },

  // === Schema Cache ===

  /**
   * Get the schemas cache object.
   */
  GET_SCHEMAS_CACHE: state => state.schemasCache,

  /**
   * Check if schemas have been loaded.
   */
  GET_SCHEMAS_LOADED: state => state.schemasCacheLoaded,

  /**
   * Get schema for a specific batch by ID.
   * Returns empty array if batch not found or has no schema.
   */
  GET_SCHEMA_FOR_BATCH: state => batchId => {
    if (!batchId || !state.schemasCache) return [];
    return state.schemasCache[batchId] || [];
  },

  // === API Data Source ===
  GET_API_DATA_SOURCES: state => state.apiDataSources,
  GET_API_DATA_SOURCES_TOTAL: state => state.apiDataSourcesTotal,
  GET_API_DATA_SOURCE_LOADING: state => state.apiDataSourceLoading,

  // === Execution History ===
  GET_EXECUTION_HISTORY: state => state.executionHistory,
  GET_EXECUTION_HISTORY_TOTAL: state => state.executionHistoryTotal,
  GET_EXECUTION_HISTORY_LOADING: state => state.executionHistoryLoading,
};
